import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Box,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    OutlinedInput,
    Grid,
    Divider,
    Avatar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AddIcon from '@mui/icons-material/Add';
import NavigationLayout from './NavigationLayout';

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [handsetList, setHandsetList] = useState([]);
    const [selectedHandset, setSelectedHandset] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // New Handset Modal State
    const [openNewHandsetModal, setOpenNewHandsetModal] = useState(false);
    const [newHandsetIDInput, setNewHandsetIDInput] = useState('');

    // Sync Diagnostic Health State
    const [syncHealth, setSyncHealth] = useState(null);
    const [checkingSync, setCheckingSync] = useState(false);

    // Interactive Chat State
    const [chatInput, setChatInput] = useState('');

    // User Management Dialog State
    const [openUserModal, setOpenUserModal] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newRole, setNewRole] = useState('USER');

    // Phone Number Dialog State
    const [openPhoneModal, setOpenPhoneModal] = useState(false);
    const [newMsisdn, setNewMsisdn] = useState('');

    // Assign MSISDN Dialog State
    const [openAssignModal, setOpenAssignModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPhoneIds, setSelectedPhoneIds] = useState([]);

    // Provisioning State
    const [initialSecret, setInitialSecret] = useState('');

    // Ref targeting ONLY the chat container element (prevents page jump)
    const chatContainerRef = useRef(null);

    const token = localStorage.getItem('authToken');

    // Scroll only the internal chat box to the bottom
    const scrollToBottomInternal = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Fetch registered handsets dynamically from database
    const fetchHandsets = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/handsets', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch handsets');
            const data = await res.json();
            const retrieved = data.handsets || [];
            setHandsetList(retrieved);

            if (retrieved.length > 0 && !selectedHandset) {
                setSelectedHandset(retrieved[0]);
            }
        } catch (err) {
            console.error('Handset fetch error:', err);
        }
    }, [token, selectedHandset]);

    const fetchData = async () => {
        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/users', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch admin data');
            const data = await res.json();
            setUsers(data.users || []);
            setPhoneNumbers(data.phoneNumbers || []);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchJobs = useCallback(async () => {
        if (!token || !selectedHandset) return;
        try {
            const url = `https://toolbox.msgg.me/api/admin/jobs?phoneId=${encodeURIComponent(selectedHandset)}`;
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch handset jobs');
            const data = await res.json();
            setJobs(data.jobs || []);
        } catch (err) {
            console.error(err);
        }
    }, [token, selectedHandset]);

    useEffect(() => {
        fetchData();
        fetchHandsets();
    }, [fetchHandsets]);

    useEffect(() => {
        if (selectedHandset) {
            fetchJobs();
            const interval = setInterval(fetchJobs, 5000);
            return () => clearInterval(interval);
        }
    }, [selectedHandset, fetchJobs]);

    // Scroll inner chat container on job updates
    useEffect(() => {
        scrollToBottomInternal();
    }, [jobs]);

    const handleRegisterNewHandsetInUI = () => {
        const formattedID = newHandsetIDInput.trim();
        if (!formattedID) return;

        if (!handsetList.includes(formattedID)) {
            setHandsetList((prev) => [...prev, formattedID]);
        }
        setSelectedHandset(formattedID);
        setNewHandsetIDInput('');
        setOpenNewHandsetModal(false);
    };

    const handleVerifySyncHealth = async () => {
        if (!selectedHandset) {
            setError('Please select or enter a Handset ID');
            return;
        }
        setCheckingSync(true);
        try {
            const res = await fetch(`https://toolbox.msgg.me/api/admin/verify-sync?phoneId=${encodeURIComponent(selectedHandset.trim())}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Verification failed');
            setSyncHealth(data);
            setError('');
        } catch (err) {
            setError(err.message);
            setSyncHealth(null);
        } finally {
            setCheckingSync(false);
        }
    };

    const handleProvisionHandset = async () => {
        if (!selectedHandset || !initialSecret) {
            setError('Please select/enter a Handset ID and enter a Secret Key');
            return;
        }

        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/provision-handset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    phoneId: selectedHandset.trim(),
                    initialSecret: initialSecret.trim()
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Provisioning failed');

            setSuccess(`Handset ${selectedHandset} successfully registered / key provisioned!`);
            setInitialSecret('');
            setError('');
            fetchHandsets();
            handleVerifySyncHealth();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSendChatMessage = async () => {
        if (!selectedHandset || !chatInput.trim()) return;

        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/dispatch-message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    targetPhoneId: selectedHandset.trim(),
                    message: chatInput.trim()
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Send failed');

            setChatInput('');
            setError('');
            await fetchJobs();
            scrollToBottomInternal();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCreateUser = async () => {
        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ username: newUsername, password: newPassword, role: newRole })
            });
            if (!res.ok) throw new Error('Failed to create user');
            setOpenUserModal(false);
            setNewUsername('');
            setNewPassword('');
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            const res = await fetch(`https://toolbox.msgg.me/api/admin/users/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to delete user');
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAddPhone = async () => {
        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/phone-numbers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ msisdn: newMsisdn })
            });
            if (!res.ok) throw new Error('Failed to add phone number');
            setOpenPhoneModal(false);
            setNewMsisdn('');
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleOpenAssignModal = (user) => {
        setSelectedUser(user);
        setSelectedPhoneIds(user.assignedNumbers.map(n => n.id));
        setOpenAssignModal(true);
    };

    const handleSaveAssignments = async () => {
        try {
            const res = await fetch('https://toolbox.msgg.me/api/admin/assign-numbers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId: selectedUser.id,
                    phoneNumberIds: selectedPhoneIds
                })
            });
            if (!res.ok) throw new Error('Failed to update assignments');
            setOpenAssignModal(false);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Chip label="Queued" color="warning" size="small" />;
            case 'DISPATCHED':
                return <Chip label="In Flight" color="info" size="small" />;
            case 'ACKNOWLEDGED':
                return <Chip label="Delivered" color="success" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const activeChatThread = [...jobs].reverse();

    return (
        <NavigationLayout title="Admin Console">
            <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
                <Typography variant="h4" gutterBottom>Handset Command & Control Console</Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Left Sidebar: Handset Selector & Provisioning */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <SmartphoneIcon color="primary" />
                                <Typography variant="h6">Target Handset</Typography>
                            </Box>

                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="select-handset-label">Select Handset</InputLabel>
                                <Select
                                    labelId="select-handset-label"
                                    value={selectedHandset}
                                    label="Select Handset"
                                    onChange={(e) => {
                                        if (e.target.value === '__REGISTER_NEW__') {
                                            setOpenNewHandsetModal(true);
                                        } else {
                                            setSelectedHandset(e.target.value);
                                            setSyncHealth(null);
                                        }
                                    }}
                                >
                                    <MenuItem value="__REGISTER_NEW__" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        <AddIcon fontSize="small" sx={{ mr: 1 }} /> + Register New Device ID
                                    </MenuItem>
                                    <Divider />
                                    {handsetList.map((id) => (
                                        <MenuItem key={id} value={id}>{id}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleVerifySyncHealth}
                                disabled={checkingSync || !selectedHandset}
                                sx={{ mb: 2 }}
                            >
                                Verify Health State
                            </Button>

                            {syncHealth && (
                                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {syncHealth.synced ? <CheckCircleIcon color="success" fontSize="small" /> : <SyncProblemIcon color="error" fontSize="small" />}
                                        Status: {syncHealth.syncState}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Last Poll: {syncHealth.secondsSinceLastPoll}s ago
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VpnKeyIcon fontSize="small" color="warning" /> Provision / Key Resync
                            </Typography>
                            <TextField
                                label="Master Secret Key"
                                type="password"
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={initialSecret}
                                onChange={(e) => setInitialSecret(e.target.value)}
                            />

                            <Button
                                variant="contained"
                                color="warning"
                                fullWidth
                                startIcon={<SyncProblemIcon />}
                                onClick={handleProvisionHandset}
                            >
                                Register / Force Resync Key
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Right Panel: Interactive Handset Chat Window */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '520px' }}>
                            <Box sx={{ pb: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
                                        <SmartphoneIcon fontSize="small" />
                                    </Avatar>
                                    Thread: <strong>{selectedHandset || 'None Selected'}</strong>
                                </Typography>
                                <Chip label="Live Auto-Sync" color="success" variant="outlined" size="small" />
                            </Box>

                            {/* Scrollable Chat Area */}
                            <Box
                                ref={chatContainerRef}
                                sx={{ flexGrow: 1, overflowY: 'auto', py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
                            >
                                {activeChatThread.length > 0 ? (
                                    activeChatThread.map((job) => {
                                        const isHandsetReply = job.action === 'HANDSET_REPLY';
                                        return (
                                            <Box
                                                key={job.id}
                                                sx={{
                                                    alignSelf: isHandsetReply ? 'flex-start' : 'flex-end',
                                                    maxWidth: '80%',
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: isHandsetReply ? '#f0f0f0' : 'primary.light',
                                                    color: isHandsetReply ? 'black' : 'white',
                                                    boxShadow: 1
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                                                    {isHandsetReply ? `📱 ${job.phone_id}` : `💻 Server (${job.action})`} • {new Date(job.created_at).toLocaleTimeString()}
                                                </Typography>

                                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {job.message}
                                                </Typography>

                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                    {getStatusBadge(job.status)}
                                                </Box>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="textSecondary">No chat history found. Select or register a handset to start.</Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Chat Input Bar */}
                            <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Send chat or command (e.g., CMD:SEND_SMS|+6591234567|Hello)..."
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSendChatMessage()}
                                    disabled={!selectedHandset}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<SendIcon />}
                                    onClick={handleSendChatMessage}
                                    disabled={!selectedHandset}
                                >
                                    Send
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* User & Phone Number Management */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5">User Management</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="contained" color="primary" onClick={() => setOpenUserModal(true)}>
                            Add User
                        </Button>
                        <Button variant="outlined" color="primary" onClick={() => setOpenPhoneModal(true)}>
                            Add Phone Number
                        </Button>
                    </Box>
                </Box>

                <Paper elevation={3} sx={{ mb: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Username</strong></TableCell>
                                <TableCell><strong>Role</strong></TableCell>
                                <TableCell><strong>Assigned MSISDNs</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u) => (
                                <TableRow key={u.id}>
                                    <TableCell>{u.id}</TableCell>
                                    <TableCell>{u.username}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={u.role}
                                            color={u.role === 'ADMIN' ? 'secondary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {u.assignedNumbers && u.assignedNumbers.length > 0 ? (
                                            u.assignedNumbers.map((num) => (
                                                <Chip key={num.id} label={num.msisdn} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">None</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{ mr: 1 }}
                                            onClick={() => handleOpenAssignModal(u)}
                                        >
                                            Assign Numbers
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleDeleteUser(u.id)}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>

                {/* Dialog: Register New Handset ID */}
                <Dialog open={openNewHandsetModal} onClose={() => setOpenNewHandsetModal(false)}>
                    <DialogTitle>Register New Target Device</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Enter a unique device identifier (e.g., <code>SG_FindN3</code> or <code>DEVICE_SG_02</code>):
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="New Handset ID"
                            fullWidth
                            value={newHandsetIDInput}
                            onChange={(e) => setNewHandsetIDInput(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenNewHandsetModal(false)}>Cancel</Button>
                        <Button onClick={handleRegisterNewHandsetInUI} variant="contained">Set Target</Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog: Add User */}
                <Dialog open={openUserModal} onClose={() => setOpenUserModal(false)}>
                    <DialogTitle>Add New System User</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Username"
                            fullWidth
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Password"
                            type="password"
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="role-select-label">Role</InputLabel>
                            <Select labelId="role-select-label" value={newRole} label="Role" onChange={(e) => setNewRole(e.target.value)}>
                                <MenuItem value="USER">USER</MenuItem>
                                <MenuItem value="ADMIN">ADMIN</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenUserModal(false)}>Cancel</Button>
                        <Button onClick={handleCreateUser} variant="contained">Create</Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog: Add Phone Number */}
                <Dialog open={openPhoneModal} onClose={() => setOpenPhoneModal(false)}>
                    <DialogTitle>Register Phone Number (MSISDN)</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="MSISDN (e.g. +6591234567)"
                            fullWidth
                            value={newMsisdn}
                            onChange={(e) => setNewMsisdn(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenPhoneModal(false)}>Cancel</Button>
                        <Button onClick={handleAddPhone} variant="contained">Add</Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog: Assign MSISDNs */}
                <Dialog open={openAssignModal} onClose={() => setOpenAssignModal(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Phone Numbers to {selectedUser?.username}</DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="assign-numbers-label">Phone Numbers</InputLabel>
                            <Select
                                labelId="assign-numbers-label"
                                multiple
                                value={selectedPhoneIds}
                                onChange={(e) => setSelectedPhoneIds(e.target.value)}
                                input={<OutlinedInput label="Phone Numbers" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const num = phoneNumbers.find((p) => p.id === value);
                                            return <Chip key={value} label={num ? num.msisdn : value} size="small" />;
                                        })}
                                    </Box>
                                )}
                            >
                                {phoneNumbers.map((phone) => (
                                    <MenuItem key={phone.id} value={phone.id}>
                                        {phone.msisdn}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenAssignModal(false)}>Cancel</Button>
                        <Button onClick={handleSaveAssignments} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </NavigationLayout>
    );
};

export default AdminDashboard;